import React, { useState, useEffect, Fragment } from "react";
import {
  Form,
  FloatingLabel,
  Card,
  Badge,
  Button,
  Modal,
  Spinner,
} from "react-bootstrap";
import { RefreshCw, RotateCcw } from "react-feather";

import DataTable from "../../components/table";
import { useNotification } from "../../components/notification";
import { useAuth } from "../../contexts/authContext";
import apiService from "../../services/apiService";
import { formatTime, formatAmount } from "../../utils";

const Transactions = () => {
  const { showNotification } = useNotification();
  const { user } = useAuth();

  const [payments, setPayments] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState({ PageNumber: 1, PageSize: 10 });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingState, setLoadingState] = useState({ action: "", id: "" });

  const [refundModal, setRefundModal] = useState({
    show: false,
    amount: "",
    paymentId: null,
    orderId: "",
    refId: "",
  });

  // Fetch payments on page load or when pagination/filter changes
  useEffect(() => {
    const fetchPayments = async () => {
      setIsLoading(true);
      try {
        const apiURL =
          user.role === ("super_admin" || "administrator")
            ? `/payments`
            : `/payments/user/${user.id}`;
        const response = await apiService.get(apiURL, {
          page: page.PageNumber,
          limit: page.PageSize,
          status: selectedStatus,
          search: searchTerm,
        });
        setPayments(response.payments); // Update payments data
        setTotalRecords(response.pagination.total); // Update total records
      } catch (error) {
        showNotification("danger", error || "Failed to get payments.", 5000);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPayments();
  }, [
    user,
    page.PageNumber,
    page.PageSize,
    selectedStatus,
    searchTerm,
    showNotification,
  ]);

  // Handle page change
  const handlePageChange = (pageNumber, pageSize) => {
    setPage({ PageNumber: pageNumber, PageSize: pageSize });
  };

  // Handle search term
  const handleSearchTerm = (text) => {
    setSearchTerm(text);
    setPage({ PageNumber: 1, PageSize: page.PageSize }); // Reset to the first page
  };

  // Handle status filter change
  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
    setPage({ PageNumber: 1, PageSize: page.PageSize }); // Reset to the first page
  };

  // Handle sync payment status
  const syncPaymentStatus = async (paymentId) => {
    setLoadingState({ action: "sync", id: paymentId });
    try {
      const response = await apiService.get(
        `/payments/sync-payment-status/${paymentId}`
      );
      setPayments((prevPayments) =>
        prevPayments.map((payment) =>
          payment.id === paymentId
            ? {
                ...payment,
                status: response.status,
              }
            : payment
        )
      );
      showNotification("success", response.message);
      if (response.shippingErrorMessage)
        showNotification(
          "danger",
          `${response.message}, but failed to create order in ShipRocket because ${response.shippingErrorMessage}`,
          20000
        );
    } catch (error) {
      showNotification("danger", error || "Failed to sync payment status.");
    } finally {
      setLoadingState(null);
    }
  };

  const handleRefund = (paymentId, orderId, refId) => {
    setRefundModal({
      show: true,
      amount: "",
      paymentId,
      orderId,
      refId,
    });
  };

  const resetRefundModal = () => {
    setRefundModal({
      show: false,
      amount: "",
      paymentId: null,
      orderId: "",
      refId: "",
    });
  };

  const processRefund = async () => {
    setLoadingState({ action: "refund" });
    try {
      const response = await apiService.get(
        `/payments/refund/${refundModal.paymentId}/${refundModal.amount}`
      );
      setPayments((prevPayments) =>
        prevPayments.map((payment) =>
          payment.id === refundModal.paymentId
            ? {
                ...payment,
                status: response.status,
              }
            : payment
        )
      );
      showNotification(
        "success",
        "The refund has been successfully processed."
      );
      resetRefundModal();
    } catch (error) {
      showNotification(
        "danger",
        error?.message || "Failed to process the refund."
      );
    } finally {
      setLoadingState(null);
    }
  };

  // Define columns for the DataTable
  const columns = [
    { accessorKey: "razorpayPaymentId", header: "Payment ID" },
    { accessorKey: "razorpayOrderId", header: "RazorPay OrderID" },
    { accessorKey: "Order.orderNumber", header: "SNC OrderNumber" },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: (info) => (
        <span>
          <span className="currency">&#8377;</span>
          {formatAmount(info.getValue())}
        </span>
      ),
    },
    // { accessorKey: "currency", header: "Currency" },
    {
      accessorKey: "status",
      header: "Status",
      cell: (info) => (
        <Badge bg={info.getValue() === "captured" ? "success" : "warning"}>
          {info.getValue().toUpperCase()}
        </Badge>
      ),
    },
    { accessorKey: "method", header: "Payment Method" },
    // {
    //   accessorKey: "refundStatus",
    //   header: "Refund Status",
    //   cell: (info) => (
    //     <Badge bg={info.getValue() === "none" ? "secondary" : "info"}>
    //       {info.getValue()}
    //     </Badge>
    //   ),
    // },
    // {
    //   accessorKey: "refundedAmount",
    //   header: "Refunded Amount",
    //   cell: (info) => (
    //     <span>
    //       <span className="currency">&#8377;</span>
    //       {formatAmount(info.getValue())}
    //     </span>
    //   ),
    // },
    {
      accessorKey: "createdAt",
      header: "Created On",
      cell: (info) => {
        const date = new Date(info.getValue());
        return formatTime(date, "short");
      },
    },
    {
      accessorKey: "updatedAt",
      header: "Updated On",
      cell: (info) => {
        const date = new Date(info.getValue());
        return formatTime(date, "short");
      },
    },
    ,
  ];

  if (user.role === "super_admin") {
    columns.push({
      accessorKey: "actions",
      header: "Actions",
      cell: (info) => (
        <Fragment>
          <Fragment>
            <Button
              variant="primary"
              size="sm"
              title="Sync Payment Status"
              className="me-1"
              onClick={() => syncPaymentStatus(info.row.original.id)}
              disabled={
                loadingState?.action === "sync" &&
                loadingState.id === info.row.original.id
              }
            >
              {loadingState?.action === "sync" &&
              loadingState.id === info.row.original.id ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <RefreshCw size={16} />
              )}
            </Button>
            {/* <Button
                variant="secondary"
                size="sm"
                title="Refund"
                onClick={() =>
                  handleRefund(
                    info.row.original.id,
                    info.row.original.razorpayOrderId,
                    info.row.original.Order.orderNumber
                  )
                }
                disabled={
                  loadingState?.action === "sync" &&
                  loadingState.id === info.row.original.id
                }
              >
                {loadingState?.action === "sync" &&
                loadingState.id === info.row.original.id ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <RotateCcw size={16} />
                )}
              </Button> */}
          </Fragment>
        </Fragment>
      ),
    });
  }

  return (
    <Fragment>
      <Card className="p-4 rounded-4">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h1 className="mb-2 fs-5">Manage Payments</h1>
              <p className="mb-0 text-light-emphasis fw-light">
                View and manage payment details.
              </p>
            </div>

            <FloatingLabel controlId="ddStatus" label="Filter by Status">
              <Form.Select
                aria-label="Filter by Status"
                value={selectedStatus}
                onChange={handleStatusChange}
                style={{ width: "150px" }}
              >
                <option value="">All</option>
                <option value="created">Created</option>
                <option value="authorized">Authorized</option>
                <option value="captured">Captured</option>
                <option value="refunded">Refunded</option>
                <option value="failed">Failed</option>
              </Form.Select>
            </FloatingLabel>
          </div>
          <hr className="my-4" />
          <DataTable
            columns={columns}
            data={payments}
            totalRecords={totalRecords}
            page={page}
            onPageChange={handlePageChange}
            loadingState={isLoading}
            searchPlaceholder="Search payments..."
            searchRecords={handleSearchTerm}
          />
        </Card.Body>
      </Card>
      <Modal
        centered
        show={refundModal.show}
        onHide={() => resetRefundModal()}
        contentClassName="p-4 border-0"
      >
        <Modal.Body>
          <h5>Refund Confirmation</h5>
          <p className="mb-3 text-muted">
            You are about to initiate a refund for <b>RazorPay Order</b>{" "}
            {refundModal.orderId} and <b>SNC Order</b> {refundModal.refId}.
          </p>
          <p className="mb-3 text-muted">
            Before proceeding, please ensure the order details are correct.
            Enter the exact amount to refund in the field below. Once the refund
            is processed, this action cannot be undone and confirm that you want
            to proceed.
          </p>
          <FloatingLabel controlId="refundAmount" label="Refund Amount">
            <Form.Control
              type="number"
              placeholder="Enter amount"
              value={refundModal.amount}
              onChange={(e) =>
                setRefundModal((prev) => ({ ...prev, amount: e.target.value }))
              }
              min="0"
              step="0.01"
              required
            />
            {(!refundModal.amount || isNaN(refundModal.amount)) && (
              <Form.Text className="text-danger">
                Please enter a valid amount.
              </Form.Text>
            )}
          </FloatingLabel>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => resetRefundModal()}
            className="btn-main"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => processRefund()}
            disabled={
              loadingState?.action === "refund" ||
              !refundModal.amount ||
              isNaN(refundModal.amount)
            }
            className="btn-main"
          >
            {loadingState?.action === "refund" ? (
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            ) : (
              "Process Refund"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default Transactions;
